import React from 'react';
import { AlertTriangle, Bot, Database } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ContentDisclaimerProps {
  variant?: 'compact' | 'full';
  showAINotice?: boolean;
  className?: string;
}

export function ContentDisclaimer({ 
  variant = 'compact', 
  showAINotice = false,
  className = ''
}: ContentDisclaimerProps) {
  if (variant === 'compact') {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-3 ${className}`}>
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-amber-800 font-medium mb-1">Information Only</p>
            <p className="text-amber-700">
              This content is for informational purposes only and does not constitute financial advice. 
              {showAINotice && ' Some content may be AI-generated.'} Always verify information independently.{' '}
              <Link to="/disclaimer" className="underline hover:text-amber-800 font-medium">
                Read full disclaimer
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="h-6 w-6 text-amber-600" />
        <h3 className="text-lg font-semibold text-amber-800">Content Disclaimer</h3>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 mb-1">Not Financial Advice</p>
            <p className="text-red-600 text-sm">
              All content is for informational purposes only. We do not provide financial, 
              investment, or trading advice. Consult qualified professionals for personalized guidance.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Database className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-700 mb-1">Public Domain Sources</p>
            <p className="text-blue-600 text-sm">
              Information is sourced from publicly available data, APIs, and financial institutions. 
              Accuracy and timeliness are not guaranteed.
            </p>
          </div>
        </div>

        {showAINotice && (
          <div className="flex items-start gap-3">
            <Bot className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-purple-700 mb-1">AI-Generated Content</p>
              <p className="text-purple-600 text-sm">
                Some articles are automatically generated using AI based on market data. 
                Always verify AI-generated information independently.
              </p>
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-amber-200">
          <p className="text-amber-700 text-xs">
            <strong>Risk Warning:</strong> Forex trading involves substantial risk of loss. 
            Only trade with money you can afford to lose. Read our{' '}
            <Link to="/disclaimer" className="underline hover:text-amber-800 font-medium">
              complete disclaimer
            </Link>{' '}
            and{' '}
            <Link to="/terms" className="underline hover:text-amber-800 font-medium">
              terms of service
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}